import { ChangeEvent, useState } from "react";

import { Card } from "@ezmid/components-library";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import { useCreateLecturerMutation } from "../../../api/lecturer.generated";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useGetImage } from "../../../hooks/useGetImage";
import { emailRegex } from "../../../utils/isEventTriggeredInElement";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { AllRoutes } from "../../route/Route";
import { LecturerDetailForm } from "../molecules/LecturerDetailForm";

type FormValues = {
  fullName: string;
  descriptionEN: string;
  descriptionDE: string;
  email: string;
  image: string;
};

export function LecturerAddPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addLecturer] = useCreateLecturerMutation();
  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);

  const FormSchema = yup.object().shape({
    fullName: yup.string().required(t("form.fullName.required")),
    email: yup
      .string()
      .matches(emailRegex, t("form.email.invalid"))
      .required(t("form.email.required")),
    descriptionEN: yup.string().required(t("form.description.required")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      fullName: "",
      descriptionEN: "",
      descriptionDE: "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { downloadedImage, isImageDownloading } = useGetImage(uploadedMediaId);

  const onSubmit = () => {
    const { descriptionEN, descriptionDE, fullName, email } = getValues();

    setIsDirtyFromImage(false);
    addLecturer({
      lecturerUpdateRequest: {
        fullName,
        contactEmail: email,
        translations: [
          {
            language: "en",
            description: descriptionEN,
          },
          {
            language: "de",
            description: descriptionDE,
          },
        ],
        lecturerImageID: uploadedMediaId || undefined,
      },
    })
      .unwrap()
      .then((res) => {
        res.id &&
          navigate(
            makeRoute(AllRoutes.LECTURER_DETAIL, {
              id: res.id,
            })
          );
        toast.success(t("app.lecturer.add.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.lecturer.add.failed"));
        }
      });
  };

  const onChangePicture = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setImageFormData(addFileToFormData(e.target.files[0]));
      setIsDirtyFromImage(true);
    }
  };

  const isImageLoading = isMediaUploading || isImageDownloading;
  const isFormDirty = isDirty || isDirtyFromImage;
  const isSubmitButtonDisabled = !isFormDirty || isImageLoading;

  return (
    <div className="w-full min-w-full flex justify-center pt-4">
      <div className="w-1/2 h-[calc(100%_-_182px)]">
        <Card shadows="xl" size="fill">
          <LecturerDetailForm
            control={control}
            errors={errors}
            onChangePicture={onChangePicture}
            handleSubmit={handleSubmit(onSubmit)}
            lecturerImage={downloadedImage || ""}
            isImageLoading={isImageLoading}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            onSumbit={onSubmit}
          />
        </Card>
      </div>
    </div>
  );
}
